// Hand-written TypeScript types mirroring proto/dive-where.proto.
// Keep them in sync; the .proto file is the canonical reference when they diverge.

// ============================================================
// Destinations
// ============================================================

export type Region =
  | "SEA"
  | "PACIFIC"
  | "JAPAN"
  | "MALDIVES"
  | "RED_SEA"
  | "AMERICAS"
  | "AFRICA"
  | "OCEANIA";

export type Tier = "MAINSTREAM" | "LESSER_KNOWN";

export type SkillLevel =
  | "SKILL_NONE"
  | "SKILL_OW"
  | "SKILL_AOW"
  | "SKILL_ADVANCED_AOW"
  | "SKILL_TECH";

export const SKILL_RANK: Record<SkillLevel, number> = {
  SKILL_NONE: 0,
  SKILL_OW: 1,
  SKILL_AOW: 2,
  SKILL_ADVANCED_AOW: 3,
  SKILL_TECH: 4,
};

export type CurrentStrength = "CURRENT_LOW" | "CURRENT_MIXED" | "CURRENT_HIGH";

export type Friendliness = "FRIENDLY_HIGH" | "FRIENDLY_MEDIUM" | "FRIENDLY_LOW";

export type EnglishRequired = "EN_NONE" | "EN_BASIC" | "EN_FLUENT";

export type VibeTag =
  | "BIG_ANIMALS"
  | "MACRO"
  | "WRECK"
  | "CAVE"
  | "DRIFT"
  | "REEF"
  | "LIVEABOARD"
  | "RESORT"
  | "CULTURE"
  | "NIGHTLIFE"
  | "REMOTE"
  | "NATURE"
  | "FIRST_DIVE";

export interface IntRange {
  min: number;
  max: number;
}

export interface Destination {
  id: string;
  name_zh: string;
  name_en: string;
  region: Region;
  tier: Tier;
  skill_min: SkillLevel;
  current_strength: CurrentStrength;
  water_temp_c: IntRange;
  chinese_friendly: Friendliness;
  english_required: EnglishRequired;
  budget_rmb_pp: IntRange;
  min_days: number;
  best_months: number[];
  vibe_tags: VibeTag[];
  marine_life: string[];
  hero_photo: string;
  gallery_photos: string[];
  oneliner_zh: string;
  why_zh: Record<string, string>;
  season_zh: string;
  level_zh: string;
  days_zh: string;
  budget_zh: string;
}

export interface DestinationCatalog {
  destinations: Destination[];
  catalog_version: string;
}

// ============================================================
// Questions
// ============================================================

export type Dimension =
  | "DIM_SKILL"
  | "DIM_VIBE"
  | "DIM_WARMTH"
  | "DIM_BUDGET"
  | "DIM_DAYS"
  | "DIM_LANGUAGE"
  | "DIM_CULTURE"
  | "DIM_CURRENT_TOLERANCE"
  | "DIM_MARINE_LIFE_SPECIFICITY"
  | "DIM_LIVEABOARD_TOLERANCE"
  | "DIM_PHOTOGRAPHY_MODE"
  | "DIM_FIRST_DIVE_CONCERNS"
  | "DIM_BEEN_THERE"
  | "DIM_WILDCARD";

export type QuestionType =
  | "SINGLE_SELECT_TILE"
  | "SINGLE_SELECT_IMAGE"
  | "SLIDER_5"
  | "SCENARIO"
  | "IMAGE_ID"
  | "TWO_CARD"
  | "MULTI_SELECT_TILE";

// QuestionId mirrors the proto enum. Listed exhaustively so TypeScript can
// catch typos and enable exhaustive switches.
export type QuestionId =
  // Required dimensions
  | "QUESTION_S1" | "QUESTION_S2" | "QUESTION_S3" | "QUESTION_S4" | "QUESTION_S5"
  | "QUESTION_V1" | "QUESTION_V2" | "QUESTION_V3"
  | "QUESTION_T1" | "QUESTION_T2"
  | "QUESTION_B1" | "QUESTION_B2"
  | "QUESTION_D1" | "QUESTION_D2"
  // Branch dimensions
  | "QUESTION_C1" | "QUESTION_C2"
  | "QUESTION_M1" | "QUESTION_M2"
  | "QUESTION_L1" | "QUESTION_L2"
  | "QUESTION_P1" | "QUESTION_P2"
  | "QUESTION_F1"
  | "QUESTION_LG1" | "QUESTION_LG2"
  | "QUESTION_CL1" | "QUESTION_CL2"
  | "QUESTION_BT1"
  // Wildcards
  | "QUESTION_W1" | "QUESTION_W2" | "QUESTION_W3" | "QUESTION_W4"
  | "QUESTION_W5" | "QUESTION_W6" | "QUESTION_W7" | "QUESTION_W8"
  | "QUESTION_W9" | "QUESTION_W10" | "QUESTION_W11" | "QUESTION_W12";

export interface QuestionOption {
  value: string;
  label_zh: string;
  emoji?: string;
  image_url?: string;
}

export interface Question {
  id: QuestionId;
  version: number;
  dimension: Dimension;
  type: QuestionType;
  text_zh: string;
  options: QuestionOption[];
  is_branch?: boolean;
  is_wildcard?: boolean;
  weight?: number;
  valid_from: string;     // ISO date
  deprecated_at?: string; // ISO date

  // Type-specific extras
  max_select?: number;     // multi-select: max picks (1 = single, default 1)
  skippable?: boolean;     // user can submit without picking (e.g., been-there)
  hero_image_url?: string; // image-id: the photo being identified
}

export interface QuestionPool {
  questions: Question[];
  pool_version: string;
}

export interface AnswerEntry {
  questionId: QuestionId;
  version: number;
  chosenValue: string;
}

// ============================================================
// API messages
// ============================================================

export interface PostSessionRequest {
  session_id: string;
  answers: AnswerEntry[];
  result_destinations: string[];
  ref?: string;
}

export interface PostFeedbackRequest {
  session_id: string;
  feedback_text: string;
  contact?: string;
  result_destination?: string;
  answer_profile?: AnswerEntry[];
  ref?: string;
}
